import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { svgIconClasses } from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { ReactNode, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import { InfoCard } from '@backstage/core-components';

import { useGetEngInsightsItems } from '../hooks/useGetEngInsightsItems';
import { ScorecardItem } from '../types';
import {
  kpiCategories,
  trendDownKPIs,
  normalizedTargetForItem,
  kpiDisplayConfig,
  kpiRouteMap,
  getMonthLabels,
  parseNumericValue,
  getRAGStatus,
  getRAGColorClass,
} from '../utils/operationalExcellenceUtils';

interface OperationalExcellenceTableProps {
  scorecardItems: ScorecardItem[];
  teamIds: string[];
  startDate: string;
  endDate: string;
  actions?: ReactNode;
  monthsToShow?: number;
  enableLinks?: boolean;
  productionEnvironment?: boolean;
}

type AggVariant = 'P75' | 'Avg';

function getLabelVariant(label: string): AggVariant | null {
  if (label.startsWith('P75 ')) return 'P75';
  if (label.startsWith('Avg ')) return 'Avg';
  return null;
}

function getBaseName(label: string): string | null {
  const variant = getLabelVariant(label);
  return variant ? label.slice(variant.length + 1) : null;
}

const useStyles = makeStyles()(theme => ({
  tableContainer: { maxHeight: 800 },
  headerCell: {
    fontWeight: 700,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#f5f7fa',
    textAlign: 'center',
    border: '1px solid rgba(224, 224, 224, 0.4)',
    padding: '6px 10px',
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  headerCellLeft: {
    textAlign: 'left',
  },
  tableCell: {
    textAlign: 'center',
    fontWeight: 600,
    color: '#ffffff',
    border: '1px solid rgba(224, 224, 224, 0.4)',
    padding: '4px 6px',
    fontSize: '0.8rem',
  },
  kpiLabelCell: {
    border: '1px solid rgba(224, 224, 224, 0.4)',
    padding: '4px 8px',
    minWidth: 220,
  },
  targetCell: {
    fontWeight: 600,
    backgroundColor: 'rgba(255, 243, 205, 0.45)',
    textAlign: 'center',
    border: '1px solid rgba(224, 224, 224, 0.4)',
    padding: '4px 8px',
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
  },
  tableRowEven: {
    backgroundColor: theme.palette.background.default,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.02)',
    },
  },
  tableRowOdd: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(0,0,0,0.025)',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.07)'
          : 'rgba(0,0,0,0.04)',
    },
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    width: '100%',
  },
  clickableLabel: {
    cursor: 'pointer',
    '&:hover .kpi-label-text': {
      textDecoration: 'underline',
      color: theme.palette.text.primary,
    },
  },
  cellGreen: { backgroundColor: '#4caf50' },
  cellAmber: { backgroundColor: '#ff9800' },
  cellRed: { backgroundColor: '#f44336' },
  cellGray: { backgroundColor: '#757575' },
  cellNA: {
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
  },
  categoryHeaderRow: {},
  categoryHeaderCell: {
    backgroundColor: '#3a6bbf !important',
    border: '1px solid rgba(224, 224, 224, 0.4)',
    padding: '5px 14px',
  },
  categoryLabel: {
    fontWeight: 700,
    fontSize: '0.78rem',
    color: '#ffffff',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
  },
  segmentedToggle: {
    display: 'inline-flex',
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.35)',
    height: 24,
    flexShrink: 0,
  },
  toggleOption: {
    padding: '0 11px',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    lineHeight: '24px',
    transition: 'background-color 0.15s, color 0.15s',
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.65)',
    borderRight: '1px solid rgba(255,255,255,0.35)',
    '&:last-child': {
      borderRight: 'none',
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.12)',
      color: '#ffffff',
    },
  },
  toggleOptionActive: {
    backgroundColor: 'rgba(255,255,255,0.22)',
    color: '#ffffff',
  },
}));

const trendIconSx = (color: string) => ({
  fontSize: 18,
  [`&.${svgIconClasses.root}`]: { color: `${color} !important` },
});

const getTrendIcon = (item: ScorecardItem): JSX.Element => {
  const validValues = item.valueOverTime
    .map(v => v.value)
    .filter(v => v !== -1 && v !== null && v !== undefined);

  if (validValues.length < 2) return <RemoveIcon sx={trendIconSx('gray')} />;

  const lastValue = parseNumericValue(validValues[validValues.length - 1]);
  const prevValue = parseNumericValue(validValues[validValues.length - 2]);

  if (lastValue === null || prevValue === null || lastValue === prevValue)
    return <RemoveIcon sx={trendIconSx('gray')} />;

  const lowerIsBetter = trendDownKPIs.has(item.label);
  const isTrendUp = lowerIsBetter
    ? lastValue < prevValue
    : lastValue > prevValue;

  return isTrendUp ? (
    <TrendingUpIcon sx={trendIconSx('#4caf50')} />
  ) : (
    <TrendingDownIcon sx={trendIconSx('#f44336')} />
  );
};

interface SegmentedVariantToggleProps {
  current: AggVariant;
  onChange: (v: AggVariant) => void;
}

const SegmentedVariantToggle: React.FC<SegmentedVariantToggleProps> = ({
  current,
  onChange,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.segmentedToggle}>
      {(['P75', 'Avg'] as AggVariant[]).map(v => (
        <button
          key={v}
          className={`${classes.toggleOption} ${
            current === v ? classes.toggleOptionActive : ''
          }`}
          onClick={e => {
            e.stopPropagation();
            onChange(v);
          }}
        >
          {v}
        </button>
      ))}
    </div>
  );
};

export const OperationalExcellenceTable: React.FC<
  OperationalExcellenceTableProps
> = ({
  scorecardItems,
  teamIds,
  startDate,
  endDate,
  actions,
  monthsToShow = 6,
  enableLinks = true,
  productionEnvironment = true,
}) => {
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeVariant, setActiveVariant] = useState<AggVariant>('Avg');

  React.useEffect(() => {
    const scrollTo = location.state?.scrollTo;
    if (scrollTo) {
      setTimeout(() => {
        document
          .getElementById(scrollTo)
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const { value: engInsightsItems, loading } = useGetEngInsightsItems(
    teamIds,
    startDate,
    endDate,
    productionEnvironment,
  );

  const itemByLabel = useMemo<Record<string, ScorecardItem>>(() => {
    const all = [...scorecardItems, ...(engInsightsItems ?? [])];
    return Object.fromEntries(all.map(i => [i.label, i]));
  }, [scorecardItems, engInsightsItems]);

  const fullKPIsList = useMemo(
    () => [...scorecardItems, ...(engInsightsItems ?? [])],
    [scorecardItems, engInsightsItems],
  );

  const monthLabels = useMemo(() => {
    if (!fullKPIsList.length) return [];
    return getMonthLabels(fullKPIsList[0].valueOverTime, monthsToShow);
  }, [fullKPIsList, monthsToShow]);

  const resolveItem = (canonicalLabel: string): ScorecardItem | undefined => {
    const variant = getLabelVariant(canonicalLabel);
    if (!variant) return itemByLabel[canonicalLabel];
    const base = getBaseName(canonicalLabel)!;
    return (
      itemByLabel[`${activeVariant} ${base}`] ?? itemByLabel[canonicalLabel]
    );
  };

  if (!fullKPIsList.length) {
    return (
      <Box style={{ padding: 16, minHeight: 500 }}>
        No KPIs found for this team
      </Box>
    );
  }

  return (
    <Box id="operational-excellence">
      <InfoCard
        title="Engineering Insights"
        variant="gridItem"
        action={actions}
      >
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Loading…
            </Typography>
          </Box>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={`${classes.headerCell} ${classes.headerCellLeft}`}
                  >
                    Metric / KPI
                  </TableCell>
                  <TableCell className={classes.headerCell}>Target</TableCell>
                  {monthLabels.map(month => (
                    <TableCell key={month} className={classes.headerCell}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell className={classes.headerCell}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      RAG
                      <Tooltip
                        title={
                          <div>
                            <div>
                              <strong>RAG: Red, Amber, Green</strong>
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <b>G:</b> &gt; 90% of target
                            </div>
                            <div>
                              <b>A:</b> 50%–89% of target
                            </div>
                            <div>
                              <b>R:</b> 0%–49% of target
                            </div>
                          </div>
                        }
                        arrow
                      >
                        <InfoIcon
                          style={{
                            fontSize: 14,
                            marginLeft: 4,
                            color: 'gray',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.headerCell}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      Trend
                      <Tooltip
                        title="Trend compares the current period to the previous period. Green up arrow means improvement."
                        arrow
                      >
                        <InfoIcon
                          style={{
                            fontSize: 14,
                            marginLeft: 4,
                            color: 'gray',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {kpiCategories.map(({ category, kpis }) => {
                  const seenBases = new Set<string>();
                  const canonicalKpis = kpis.filter(label => {
                    const variant = getLabelVariant(label);
                    if (!variant) return true;
                    const base = getBaseName(label)!;
                    if (seenBases.has(base)) return false;
                    seenBases.add(base);
                    return true;
                  });

                  const categoryItems = canonicalKpis
                    .map(label => ({
                      canonical: label,
                      item: resolveItem(label),
                    }))
                    .filter(
                      (x): x is { canonical: string; item: ScorecardItem } =>
                        !!x.item,
                    );

                  if (categoryItems.length === 0) return null;

                  const hasDualVariant = kpis.some(
                    l => getLabelVariant(l) !== null,
                  );

                  return (
                    <React.Fragment key={category}>
                      <TableRow className={classes.categoryHeaderRow}>
                        <TableCell
                          colSpan={monthLabels.length + 4}
                          className={classes.categoryHeaderCell}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <span className={classes.categoryLabel}>
                              {category}
                            </span>
                            {hasDualVariant && (
                              <SegmentedVariantToggle
                                current={activeVariant}
                                onChange={setActiveVariant}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>

                      {categoryItems.map(({ canonical, item }, index) => {
                        const base = getBaseName(canonical);
                        const effectiveLabel = item.label;
                        const lastValueNumeric = parseNumericValue(
                          item.valueOverTime.slice(-1)[0]?.value,
                        );
                        const ragStatus = getRAGStatus(
                          effectiveLabel,
                          lastValueNumeric,
                        );
                        const config = kpiDisplayConfig[item.label];
                        const isClickable =
                          enableLinks && !!kpiRouteMap[item.label];

                        return (
                          <TableRow
                            key={canonical}
                            className={
                              index % 2 === 0
                                ? classes.tableRowEven
                                : classes.tableRowOdd
                            }
                          >
                            <TableCell
                              className={`${classes.kpiLabelCell} ${
                                isClickable ? classes.clickableLabel : ''
                              }`}
                              onClick={() => {
                                if (isClickable)
                                  navigate(kpiRouteMap[item.label]);
                              }}
                            >
                              <Box display="flex" alignItems="center">
                                <Tooltip
                                  title={config?.desc || ''}
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon
                                    style={{
                                      fontSize: 14,
                                      marginRight: 6,
                                      color: 'gray',
                                      cursor: 'default',
                                      flexShrink: 0,
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </Tooltip>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  className="kpi-label-text"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '0.82rem',
                                  }}
                                >
                                  {base ?? item.label}
                                  {config ? ` (${config.unit})` : ''}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell className={classes.targetCell}>
                              {normalizedTargetForItem[effectiveLabel] ??
                                normalizedTargetForItem[item.label] ??
                                'N/A'}
                            </TableCell>

                            {monthLabels.map(monthLabel => {
                              const monthValue = item.valueOverTime.find(
                                v =>
                                  new Date(v.date).toLocaleString('default', {
                                    month: 'short',
                                  }) === monthLabel,
                              );
                              const numericValue = parseNumericValue(
                                monthValue?.value,
                              );
                              const ragStatusForCell = getRAGStatus(
                                effectiveLabel,
                                numericValue,
                              );
                              const colorClass = getRAGColorClass(
                                classes,
                                ragStatusForCell,
                              );

                              return (
                                <TableCell
                                  key={monthLabel}
                                  className={`${classes.tableCell} ${colorClass}`}
                                >
                                  {numericValue !== null ? numericValue : 'N/A'}
                                </TableCell>
                              );
                            })}

                            <TableCell
                              className={`${
                                classes.tableCell
                              } ${getRAGColorClass(classes, ragStatus)}`}
                            >
                              {ragStatus}
                            </TableCell>

                            <TableCell
                              className={classes.tableCell}
                              style={{
                                backgroundColor: 'transparent',
                                color: 'inherit',
                              }}
                            >
                              {getTrendIcon(item)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfoCard>
    </Box>
  );
};
